import {Box, Card, DialogActions, DialogContent, DialogTitle, Input, Stack, Tab, Tabs, Typography} from "@mui/material";
import React, {FunctionComponent, useCallback, useEffect, useMemo, useState} from "react";
import {ReactSortable} from "react-sortablejs";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Breakpoint} from "@mui/system/createTheme/createBreakpoints";
import Utils from "../../../../../../../../core/services/utils/utils";
import Dialog from "../../../../../../../components/base/dialog";
import Button from "../../../../../../../components/base/button/button";
import {DashboardWidgetType} from "../../types";

export interface ManageDashboardLayoutWidget {
    key: string;
    title: string;
    size?: { [key in Breakpoint]?: number };
    minSize?: { [key in Breakpoint]?: number };
    type: DashboardWidgetType;
}

export interface ManageDashboardLayoutDialogProps {
    open: boolean;
    close: () => void;
    title: string;
    widgets: Array<ManageDashboardLayoutWidget>;
    onRearrange: (widgets: ManageDashboardLayoutWidget[]) => Promise<boolean>;
}


interface ListEntry extends ManageDashboardLayoutWidget {
    id: string;
    orderIndex: number;
}

/**
 * Function to get the current breakpoint based on the window width
 */
const getCurrentBreakpoint = () => {
    const width = window.innerWidth;
    if (width < 600) return 'xs';
    if (width < 1200) return 'md';
    if (width < 1536) return 'lg';
    return 'xl';
};

const ManageDashboardLayoutDialog: FunctionComponent<ManageDashboardLayoutDialogProps> = ({open, close, title, widgets, onRearrange}) => {
    const [entries, setEntries] = useState<ListEntry[]>(widgets?.map((e, i) => ({...e, id: e.key, orderIndex: i})) ?? []);
    const [breakpoint, setBreakpoint] = useState<Breakpoint>(getCurrentBreakpoint());

    /**
     * Handles the size change of a widget
     * @param widgetKey
     * @param breakpoint
     * @param newSize
     */
    const onSizeChange = (widgetKey: string, breakpoint: Breakpoint, newSize: number) => {
        setEntries(prevEntries =>
            prevEntries.map(entry =>
                entry.key === widgetKey
                    ? {
                        ...entry,
                        size: {
                            ...entry.size,
                            [breakpoint]: newSize
                        }
                    }
                    : entry
            )
        );
    };

    const breakpointTitleMap = useMemo(() => ({
        xs: "Mobile",
        md: 'Tablet',
        lg: "Laptop",
        xl: "Desktop",
    }), [])

    /**
     * With each change in the list, update the state
     */
    useEffect(() => {
        if (!open) {
            return setEntries([]);
        }
        setBreakpoint(getCurrentBreakpoint());
        setEntries(widgets?.map((e, i) => ({...e, id: e.key, orderIndex: i})) ?? [])
    }, [widgets, open]);

    /**
     * Calls the provided rearranging callback
     * @type {(function(): void)|*}
     */
    const saveChanges = useCallback(async () => {
        const rearrangedKeys = entries.map(entry => entry.key);
        const res: ManageDashboardLayoutWidget[] = [];
        for (const entry of widgets) {
            const index = rearrangedKeys.indexOf(entry.key);
            const listEntry = entries[index];
            res[index] = {...entry, size: listEntry.size};
        }
        const resultFlag = await onRearrange(res);
        if (resultFlag) {
            close();
        }
    }, [close, widgets, entries, onRearrange]);

    /**
     * Updates the list entries based on the drag and drop event
     * @type {(function(*): void)|*}
     */
    const updateListEntries = useCallback((e) => setEntries(prevState => Utils.reorderList(prevState, e.oldDraggableIndex, e.newDraggableIndex, 'orderIndex')), [])

    /**
     * Returns the size of the entry based on the breakpoint
     */
    const getSize = useCallback((size: number | undefined | null): number => {
        if (size === undefined || size === null) {
            return 12;
        }
        if (size > 12) {
            return 12;
        }
        return size;
    }, [])

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'md'}
                className={'bound-dialog rearrange-entries-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'} component={'div'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {title ?? "Rearrange Entries"}
                    </p>
                </DialogTitle>
                <DialogContent className={'p-3 d-flex flex-column align-items-center w-100'} dividers>
                    <Tabs
                        sx={{width: '100%', marginBottom: 2}}
                        variant={'fullWidth'}
                        value={breakpoint}
                        onChange={(e, val) => setBreakpoint(val)}
                    >
                        {
                            Object.keys(breakpointTitleMap).map((key) => (
                                <Tab key={key} value={key} label={breakpointTitleMap[key as Breakpoint] ?? ""}/>
                            ))
                        }
                    </Tabs>
                    <Grid2
                        container
                        spacing={1}
                        component={ReactSortable}
                        sort
                        className={'entries-container'}
                        list={entries}
                        setList={() => void 0}
                        onUpdate={updateListEntries}
                        handle={'.handle'}
                        group={'entries'}
                        animation={210}
                        forceFallback={false}
                        ghostClass={"dragged-entry"}
                    >
                        {
                            entries.map((entry) => (
                                <Grid2 key={entry.key} xs={getSize(entry.size?.[breakpoint])}>
                                    <Card
                                        variant={'outlined'}
                                        className={'handle'}
                                        sx={(theme) => ({
                                            padding: 1,
                                            backgroundColor: `rgba(${theme.palette.primary.mainChannel} / ${theme.palette.action.hoverOpacity})`,
                                            borderRadius: 1,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        })}
                                    >
                                        <Typography variant="body2" fontWeight={600} color={'primary.main'} textAlign={'center'} marginBlockEnd={1}>
                                            {entry.title ?? ""}
                                        </Typography>
                                        <Stack direction='column' alignItems='center' justifyContent='center' sx={{width: '100%'}}>
                                            {
                                                Object.keys(breakpointTitleMap).filter((key) => key === breakpoint).map((key) => (
                                                    <EditableGridSize
                                                        key={key}
                                                        minSize={entry.minSize?.[key as Breakpoint] ?? 1}
                                                        size={entry.size?.[key as Breakpoint] ?? 12}
                                                        onChange={(newSize) => onSizeChange(entry.key, key as Breakpoint, newSize)}
                                                    />
                                                ))
                                            }
                                        </Stack>

                                    </Card>
                                </Grid2>
                            ))
                        }
                    </Grid2>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            onClick={() => close()}
                            borderRadius={'0 8px 0 8px'}
                        >
                            Back
                        </Button>
                        <Button
                            variant={'dialog'}
                            color={'primary'}
                            borderRadius={'8px 0 8px 0'}
                            onClick={() => saveChanges()}
                        >
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ManageDashboardLayoutDialog;


interface EditableGridSizeProps {
    size: number;
    minSize?: number;
    onChange: (newSize: number) => void;
}

const EditableGridSize: React.FC<EditableGridSizeProps> = ({size, minSize, onChange}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(size.toString());

    /**
     * Enables editing mode on double click
     */
    const handleDoubleClick = useCallback(() => setIsEditing(true), []);

    /**
     * Exits editing mode and updates the value
     */
    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        setIsEditing(false);
        const newValue = Math.min(12, Math.max(minSize ?? 1, parseInt(e.target.value?.toString()) || (minSize ?? 1)));
        onChange(newValue);
        // eslint-disable-next-line
    }, [value]);

    /**
     * Handles the Enter key to save changes and exit editing mode
     */
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleBlur(e as any);
        }
    }, [handleBlur]);

    /**
     * Handles the change event for the input field
     * @param e
     */
    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value), []);

    return (
        <Typography
            width={'100%'}
            variant="caption"
            fontWeight={600}
            color={'tertiary.main'}
            textAlign={'center'}
            onDoubleClick={handleDoubleClick}
            sx={{cursor: 'pointer'}}
        >
            Size: {
            isEditing
                ? <Input
                    type="number"
                    sx={{input: {textAlign: 'center'}}}
                    value={value}
                    inputProps={{min: minSize ?? 1, max: 12}}
                    autoFocus
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                />
                : size.toString()
        }
        </Typography>
    );
};
